import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

// Feature flags for the PAA flow.

interface AppConfig {
  googleMapsApiKey: string | null;
  whatChangedEnabled: boolean;
}

/** The "what changed" comparison: upload the approved set → per-sheet change
 *  notes, the amendment summary, per-row re-compare, and the side-by-side
 *  drawing viewer.
 *
 *  SERVER-DRIVEN, not a compile-time constant. It used to be `export const
 *  WHAT_CHANGED_ENABLED = true`, which meant turning it on or off was a rebuild
 *  and a deploy — the wrong shape for the one feature here that depends on an
 *  external account being in place. Now the whole surface can ship dark, be
 *  turned on the day the OpenRouter key lands, and be turned off in seconds if a
 *  filing goes wrong, all without shipping anything.
 *
 *  Set `Features__WhatChanged=false` to hide it. Defaults ON server-side, so an
 *  environment that never sets it behaves exactly as before.
 *
 *  The backend endpoints stay wired either way; this only controls the UI. */
export function useWhatChangedEnabled(): boolean {
  const { data } = useQuery<AppConfig>({
    queryKey: ["config"],
    queryFn: () => api<AppConfig>("/api/config"),
    staleTime: Infinity,
  });

  // Hidden until the server has actually said yes. The opposite default would
  // flash the entire feature on screen before hiding it again in exactly the
  // deployment where it is meant to be off — which is the one place a flicker
  // would be alarming rather than cosmetic.
  return data?.whatChangedEnabled ?? false;
}
